import {
  AdvancedReportMetricRow,
  AdvancedReportMetricRowsResult
} from 'features/advanced-reports/advanced-reports.types';
import {
  AdvancedReportTableCell,
  AdvancedReportTableRow
} from '../advanced-reports-table.types';
import {
  calculateCpaBase,
  calculateCpsBase,
  calculateRoasBase,
  calculateRoiBase
} from 'features/reports/functions/metric-calculation-functions';
import { MetricAttribution } from 'common/types/metric.models';
import { MetricTypes } from 'common/constants/metric-types';

function getSummedMetricValuesById(
  metrics: AdvancedReportMetricRow[],
  metricId: string
): number {
  const summedMetrics = metrics
    .filter((metric) => metric.metricId === metricId)
    .map((metric) => metric.metricValue)
    .reduce((total, metricValue) => (total += metricValue), 0);

  return summedMetrics;
}

function createCellByMetricId(
  primaryMetrics: AdvancedReportMetricRow[],
  compareMetrics: AdvancedReportMetricRow[],
  metricId: string
): AdvancedReportTableCell {
  const cell: AdvancedReportTableCell = {
    primaryValue: getSummedMetricValuesById(primaryMetrics, metricId),
    compareValue: getSummedMetricValuesById(compareMetrics, metricId)
  };

  return cell;
}

export const createRows = (
  primaryMetrics: AdvancedReportMetricRowsResult,
  compareMetrics: AdvancedReportMetricRowsResult
): AdvancedReportTableRow[] => {
  const allMetrics = [
    ...primaryMetrics.impressions,
    ...primaryMetrics.clicks,
    ...primaryMetrics.calls,
    ...primaryMetrics.webs,
    ...primaryMetrics.opps,
    ...primaryMetrics.oppValues,
    ...primaryMetrics.sales,
    ...primaryMetrics.revenues,
    ...primaryMetrics.costs,
    ...compareMetrics.impressions,
    ...compareMetrics.clicks,
    ...compareMetrics.calls,
    ...compareMetrics.webs,
    ...compareMetrics.opps,
    ...compareMetrics.oppValues,
    ...compareMetrics.sales,
    ...compareMetrics.revenues,
    ...compareMetrics.costs
  ];

  const unqiueMetricIdsSet = new Set<string>([
    ...allMetrics.map((metric) => metric.metricId)
  ]);

  const unqiqueMetricIds = [...unqiueMetricIdsSet];
  const metricRows = unqiqueMetricIds.map((metricId) => {
    const metric = allMetrics.find((m) => m.metricId === metricId)!;
    const call = createCellByMetricId(
      primaryMetrics.calls,
      compareMetrics.calls,
      metricId
    );
    const web = createCellByMetricId(
      primaryMetrics.webs,
      compareMetrics.webs,
      metricId
    );
    const cost = createCellByMetricId(
      primaryMetrics.costs,
      compareMetrics.costs,
      metricId
    );
    const revenue = createCellByMetricId(
      primaryMetrics.revenues,
      compareMetrics.revenues,
      metricId
    );
    const sales = createCellByMetricId(
      primaryMetrics.sales,
      compareMetrics.sales,
      metricId
    );

    const row: AdvancedReportTableRow = {
      id: metricId,
      parentId: metric.metricParentId,
      hasChildren: allMetrics.some((m) => m.metricParentId === metricId),
      depth: metric.depth,
      field: metric.metricTypeName,
      shouldBlurField: metric.isMetricTypeNameSensitive,
      impressions: createCellByMetricId(
        primaryMetrics.impressions,
        compareMetrics.impressions,
        metricId
      ),
      clicks: createCellByMetricId(
        primaryMetrics.clicks,
        compareMetrics.clicks,
        metricId
      ),
      call,
      web,
      opportunity: createCellByMetricId(
        primaryMetrics.opps,
        compareMetrics.opps,
        metricId
      ),
      opportunityValue: createCellByMetricId(
        primaryMetrics.oppValues,
        compareMetrics.oppValues,
        metricId
      ),
      sales,
      revenue,
      cost,
      cpa: {
        primaryValue: calculateCpaBase(
          cost.primaryValue,
          web.primaryValue,
          call.primaryValue
        ),
        compareValue: calculateCpaBase(
          cost.compareValue,
          web.compareValue,
          call.compareValue
        )
      },
      cps: {
        primaryValue: calculateCpsBase(cost.primaryValue, sales.primaryValue),
        compareValue: calculateCpsBase(cost.compareValue, sales.compareValue)
      },
      roi: {
        primaryValue: calculateRoiBase(cost.primaryValue, revenue.primaryValue),
        compareValue: calculateRoiBase(cost.compareValue, revenue.compareValue)
      },
      roas: {
        primaryValue: calculateRoasBase(
          cost.primaryValue,
          revenue.primaryValue
        ),
        compareValue: calculateRoasBase(cost.compareValue, revenue.compareValue)
      }
    };

    return row;
  });

  return metricRows;
};

export const createTotalRow = (
  rows: AdvancedReportTableRow[]
): AdvancedReportTableRow => {
  const call = createCell(
    rows.map((row) => row.call.primaryValue),
    rows.map((row) => row.call.compareValue)
  );
  const web = createCell(
    rows.map((row) => row.web.primaryValue),
    rows.map((row) => row.web.compareValue)
  );
  const sales = createCell(
    rows.map((row) => row.sales.primaryValue),
    rows.map((row) => row.sales.compareValue)
  );
  const revenue = createCell(
    rows.map((row) => row.revenue.primaryValue),
    rows.map((row) => row.revenue.compareValue)
  );
  const cost = createCell(
    rows.map((row) => row.cost.primaryValue),
    rows.map((row) => row.cost.compareValue)
  );

  const totalRow: AdvancedReportTableRow = {
    id: 'total',
    parentId: '',
    hasChildren: false,
    depth: 0,
    field: 'Total',
    shouldBlurField: false,
    impressions: createCell(
      rows.map((row) => row.impressions.primaryValue),
      rows.map((row) => row.impressions.compareValue)
    ),
    clicks: createCell(
      rows.map((row) => row.clicks.primaryValue),
      rows.map((row) => row.clicks.compareValue)
    ),
    call,
    web,
    opportunity: createCell(
      rows.map((row) => row.opportunity.primaryValue),
      rows.map((row) => row.opportunity.compareValue)
    ),
    opportunityValue: createCell(
      rows.map((row) => row.opportunityValue.primaryValue),
      rows.map((row) => row.opportunityValue.compareValue)
    ),
    sales,
    revenue,
    cost,
    cpa: {
      primaryValue: calculateCpaBase(
        cost.primaryValue,
        web.primaryValue,
        call.primaryValue
      ),
      compareValue: calculateCpaBase(
        cost.compareValue,
        web.compareValue,
        call.compareValue
      )
    },
    cps: {
      primaryValue: calculateCpsBase(cost.primaryValue, sales.primaryValue),
      compareValue: calculateCpsBase(cost.compareValue, sales.compareValue)
    },
    roi: {
      primaryValue: calculateRoiBase(cost.primaryValue, revenue.primaryValue),
      compareValue: calculateRoiBase(cost.compareValue, revenue.compareValue)
    },
    roas: {
      primaryValue: calculateRoasBase(cost.primaryValue, revenue.primaryValue),
      compareValue: calculateRoasBase(cost.compareValue, revenue.compareValue)
    }
  };

  return totalRow;
};

function createCell(
  primaryValues: number[],
  compareValues: number[]
): AdvancedReportTableCell {
  const cell: AdvancedReportTableCell = {
    primaryValue: primaryValues.reduce<number>(
      (total, current) => (total += current),
      0
    ),
    compareValue: compareValues.reduce<number>(
      (total, current) => (total += current),
      0
    )
  };

  return cell;
}

export const createMetricAttributions = (
  attributionModel: string,
  metricRows: AdvancedReportMetricRow[],
  metricType: MetricTypes
): MetricAttribution[] => {
  const attributions = metricRows.map((metric) => {
    return {
      attributionModelName: attributionModel,
      attributionValue: metric.metricValue,
      metricType,
      metricTypeName: metric.metricTypeName
    };
  });

  return attributions;
};

export const createAllMetricAttributionsFromArMetricRows = (
  attributionModel: string,
  impressions: AdvancedReportMetricRow[],
  clicks: AdvancedReportMetricRow[],
  calls: AdvancedReportMetricRow[],
  forms: AdvancedReportMetricRow[],
  opps: AdvancedReportMetricRow[],
  oppValues: AdvancedReportMetricRow[],
  sales: AdvancedReportMetricRow[],
  revenues: AdvancedReportMetricRow[],
  costs: AdvancedReportMetricRow[]
): MetricAttribution[] => {
  const impressionAttributions = impressions.map((imp) => {
    return {
      attributionModelName: attributionModel,
      attributionValue: imp.metricValue,
      metricType: MetricTypes.IMPRESSIONS,
      metricTypeName: imp.metricTypeName
    };
  });

  const clickAttributions = clicks.map((click) => {
    return {
      attributionModelName: attributionModel,
      attributionValue: click.metricValue,
      metricType: MetricTypes.CLICKS,
      metricTypeName: click.metricTypeName
    };
  });

  const callAttributions = calls.map((call) => {
    return {
      attributionModelName: attributionModel,
      attributionValue: call.metricValue,
      metricType: MetricTypes.INBOUND_CALL,
      metricTypeName: call.metricTypeName
    };
  });

  const formsAttributions = forms.map((form) => {
    return {
      attributionModelName: attributionModel,
      attributionValue: form.metricValue,
      metricType: MetricTypes.WEB,
      metricTypeName: form.metricTypeName
    };
  });

  const oppsAttributions = opps.map((opp) => {
    return {
      attributionModelName: attributionModel,
      attributionValue: opp.metricValue,
      metricType: MetricTypes.OPPORTUNITIES,
      metricTypeName: opp.metricTypeName
    };
  });

  const oppValuesAttributions = oppValues.map((oppValue) => {
    return {
      attributionModelName: attributionModel,
      attributionValue: oppValue.metricValue,
      metricType: MetricTypes.OPPORTUNITY_VALUES,
      metricTypeName: oppValue.metricTypeName
    };
  });

  const salesAttributions = sales.map((sale) => {
    return {
      attributionModelName: attributionModel,
      attributionValue: sale.metricValue,
      metricType: MetricTypes.REVENUES,
      metricTypeName: sale.metricTypeName
    };
  });

  const revenuesAttributions = revenues.map((rev) => {
    return {
      attributionModelName: attributionModel,
      attributionValue: rev.metricValue,
      metricType: MetricTypes.REVENUE_VALUES,
      metricTypeName: rev.metricTypeName
    };
  });

  const costsAttributions = costs.map((cost) => {
    return {
      attributionModelName: attributionModel,
      attributionValue: cost.metricValue,
      metricType: MetricTypes.COST,
      metricTypeName: cost.metricTypeName
    };
  });

  return [
    ...impressionAttributions,
    ...clickAttributions,
    ...callAttributions,
    ...formsAttributions,
    ...oppsAttributions,
    ...oppValuesAttributions,
    ...salesAttributions,
    ...revenuesAttributions,
    ...costsAttributions
  ];
};
